// Table view (04 §3) — the same leads, as a spreadsheet.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStages } from '../sales/useSales.js';
import type { LeadListRow } from './useLeads.js';

type SortKey = 'business_name' | 'lead_score' | 'rating' | 'tier' | 'priority' | 'area' | 'phone';

const STORAGE_KEY = 'leadg.table.sort';

function readSaved(): { key: SortKey; asc: boolean } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== null) return JSON.parse(raw) as { key: SortKey; asc: boolean };
  } catch {
    // ignore
  }
  return { key: 'lead_score', asc: false };
}

function compare(a: LeadListRow, b: LeadListRow, key: SortKey): number {
  if (key === 'lead_score') return (a.lead_score ?? -1) - (b.lead_score ?? -1);
  if (key === 'rating') return (a.rating ?? -1) - (b.rating ?? -1);
  const left = String(a[key] ?? '');
  const right = String(b[key] ?? '');
  return left.localeCompare(right, 'bg');
}

function ownerLabel(lead: LeadListRow): string {
  const ent = lead.legal_entities;
  if (!ent || (ent.match_confidence ?? 0) < 55) return '—';
  const person = ent.manager_names?.[0] ?? ent.owner_names?.[0] ?? ent.legal_name ?? ent.eik ?? '—';
  const caveat = (ent.match_confidence ?? 0) < 80 ? ' (?)' : '';
  return `${person}${ent.eik ? ` · ${ent.eik}` : ''}${caveat}`;
}

function SortHeader({
  label,
  sortKey,
  current,
  onToggle,
}: {
  label: string;
  sortKey: SortKey;
  current: { key: SortKey; asc: boolean };
  onToggle: (key: SortKey) => void;
}) {
  const arrow = current.key === sortKey ? (current.asc ? ' ▲' : ' ▼') : '';
  return (
    <button type="button" onClick={() => onToggle(sortKey)}>
      {label}
      {arrow}
    </button>
  );
}

function LeadTableRow({
  lead,
  stageName,
  onSelect,
}: {
  lead: LeadListRow;
  stageName: string;
  onSelect: () => void;
}) {
  const tierClass =
    lead.tier === 'A' ? 'badge--tier-a' : lead.tier === 'B' ? 'badge--tier-b' : 'badge--tier-c';
  const hasRating = lead.rating !== null && lead.rating !== undefined;

  return (
    <tr onClick={onSelect} style={{ cursor: 'pointer' }}>
      <td style={{ fontWeight: 600 }}>{lead.business_name}</td>
      <td>{lead.area ?? lead.city_key}</td>
      <td>{lead.tier ? <span className={`badge ${tierClass}`}>{lead.tier}</span> : '—'}</td>
      <td className="tbl__num">
        {lead.lead_score !== null ? (
          <span className="badge badge--score">{lead.lead_score}</span>
        ) : (
          '—'
        )}
      </td>
      <td className="tbl__num">
        {hasRating ? (
          <span>
            {lead.rating} ★ ({lead.review_count ?? 0})
          </span>
        ) : (
          '—'
        )}
      </td>
      <td>{stageName}</td>
      <td>{ownerLabel(lead)}</td>
      <td>{lead.priority ? <span className="badge badge--neutral">{lead.priority}</span> : '—'}</td>
      <td style={{ fontFamily: 'var(--font-mono)' }}>{lead.phone ?? '—'}</td>
      <td>
        {lead.website_url ? (
          <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
            {lead.domain_norm ?? 'да'}
          </span>
        ) : (
          <span style={{ color: 'var(--fg-subtle)' }}>няма</span>
        )}
      </td>
    </tr>
  );
}

export function LeadsTable({ leads }: { leads: readonly LeadListRow[] }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState(readSaved);
  const { data: stages = [] } = useStages();
  const stageNameById = new Map(stages.map((s) => [s.id, s.name]));

  const toggle = (key: SortKey): void => {
    const next =
      sort.key === key
        ? { key, asc: !sort.asc }
        : { key, asc: key !== 'lead_score' && key !== 'rating' };
    setSort(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const rows = [...leads].sort((a, b) => (sort.asc ? 1 : -1) * compare(a, b, sort.key));

  return (
    <div className="tbl__wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>
              <SortHeader label="Име" sortKey="business_name" current={sort} onToggle={toggle} />
            </th>
            <th>
              <SortHeader label="Район" sortKey="area" current={sort} onToggle={toggle} />
            </th>
            <th>
              <SortHeader label="Клас" sortKey="tier" current={sort} onToggle={toggle} />
            </th>
            <th className="tbl__num">
              <SortHeader label="Точки" sortKey="lead_score" current={sort} onToggle={toggle} />
            </th>
            <th className="tbl__num">
              <SortHeader label="Рейтинг" sortKey="rating" current={sort} onToggle={toggle} />
            </th>
            <th>Етап</th>
            <th>Собственик / ЕИК</th>
            <th>
              <SortHeader label="Приоритет" sortKey="priority" current={sort} onToggle={toggle} />
            </th>
            <th>
              <SortHeader label="Телефон" sortKey="phone" current={sort} onToggle={toggle} />
            </th>
            <th>Уебсайт</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((lead) => (
            <LeadTableRow
              key={lead.id}
              lead={lead}
              stageName={stageNameById.get(lead.sales_stage_id ?? -1) ?? '—'}
              onSelect={() => navigate(`/leads/${lead.id}`)}
            />
          ))}
        </tbody>
      </table>

      {rows.length === 0 && <p className="tbl__empty">Няма обекти по тези филтри.</p>}
    </div>
  );
}
